import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useMenu } from "@/menu/MenuApp";
import { loc } from "@/menu/i18n";

export const AllergenBadges = ({ codes, allergens, lang }) => {
  if (!codes?.length) return null;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1.5">
      {codes.map((code) => {
        const a = allergens.find((x) => x.code === code);
        return (
          <span key={code} title={lang === "en" ? a?.en : a?.[lang] || a?.it} className="rounded-full border border-[#C19D60]/25 px-2 py-0.5 text-[9px] font-light tracking-wide text-[#C19D60]/90">
            {String(code).padStart(2, "0")} · {lang === "en" ? a?.en : a?.[lang] || a?.it}
          </span>
        );
      })}
    </div>
  );
};

export const DishCard = ({ dish, index, allergens, lang, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ duration: 0.7, delay: 0.05 * (index % 3), ease: [0.22, 1, 0.36, 1] }}
  >
    <Link
      to={`/menu/piatto/${dish.id}`}
      data-testid={`dish-${dish.id}`}
      className={`group flex gap-4 rounded-lg border border-white/5 bg-[#101012] p-3.5 transition-colors duration-500 hover:border-[#C19D60]/25 ${!dish.available ? "opacity-55" : ""}`}
    >
    {dish.image && (
      <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl sm:h-28 sm:w-28">
        <img src={dish.image} alt={loc(dish.name, lang)} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-lg font-medium leading-snug">{loc(dish.name, lang)}</h3>
        <span className="font-display shrink-0 text-lg italic text-[#C19D60]">{Number.isInteger(dish.price) ? dish.price : dish.price.toFixed(1)}€</span>
      </div>
      <p className="mt-1 text-[13px] font-light leading-relaxed text-[#A3A3A3]">{loc(dish.description, lang)}</p>
      {!dish.available && <p className="mt-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-red-400/80">{t("unavailable")}</p>}
      <AllergenBadges codes={dish.allergens} allergens={allergens} lang={lang} />
    </div>
    </Link>
  </motion.div>
);

const CategoryPage = () => {
  const { slug } = useParams();
  const { data, lang, t } = useMenu();
  const cat = data.categories.find((c) => (c.slug || c.id.replace("cat-", "")) === slug);
  if (!cat) return <div className="px-6 py-20 text-center text-[#A3A3A3]">—</div>;
  const dishes = data.dishes.filter((d) => d.category_id === cat.id).sort((a, b) => a.order - b.order);
  const num = String(data.categories.indexOf(cat) + 1).padStart(2, "0");

  return (
    <div data-testid="menu-category-page">
      <div className="relative h-56 overflow-hidden sm:h-72">
        <img src={cat.image} alt={loc(cat.name, lang)} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-black/40 to-black/30" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-5xl px-6 pb-6">
          <span className="font-display text-lg italic text-[#C19D60]">{num}</span>
          <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">{loc(cat.name, lang)}</h1>
        </div>
      </div>
      <div className="mx-auto max-w-5xl px-5 py-10">
        <Link to="/menu" data-testid="menu-back-link" className="mb-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors hover:text-[#E5CAA4]">
          <ArrowLeft size={14} /> {t("backToMenu")}
        </Link>
        <div className="grid gap-3.5 md:grid-cols-2" data-testid="menu-dish-list">
          {dishes.map((d, i) => (
            <DishCard key={d.id} dish={d} index={i} allergens={data.allergens} lang={lang} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
